"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useMounted = useMounted;
exports.usePostData = usePostData;
exports.useLoadData = useLoadData;
exports.useResetData = useResetData;
exports.useGetData = useGetData;
exports.useDeleteData = useDeleteData;
exports.useUpdateData = useUpdateData;
exports.useSuggestUsers = useSuggestUsers;
exports.useSearchParam = useSearchParam;
exports.useSearchParamState = useSearchParamState;
exports.useInterval = useInterval;
exports.useTab = useTab;
const zustand_1 = require("zustand");
const react_1 = require("react");
const react_router_dom_1 = require("react-router-dom");
const http_1 = require("./http");
function removeAll(path, oldData) {
    const newData = {};
    const skipped = Object.entries(oldData).filter(([key, value]) => {
        if (!key.startsWith(path)) {
            newData[key] = value;
            return false;
        }
        return true;
    });
    return skipped.length ? newData : oldData;
}
const useStore = (0, zustand_1.create)((set, get) => ({
    data: {},
    async addData(path, details) {
        await (0, http_1.createAny)(path, details);
        const data = removeAll(path, get().data);
        set({ data });
    },
    async updateData(path, id, details) {
        await (0, http_1.updateAny)(`${path}/${id}`, details);
        const data = removeAll(path, get().data);
        set({ data });
    },
    async deleteData(path, id) {
        await (0, http_1.deleteAny)(`${path}/${id}`);
        const data = removeAll(path, get().data);
        set({ data });
    },
    resetData(paths) {
        let data = get().data;
        for (const path of paths) {
            data = removeAll(path, data);
        }
        set({ data });
    },
    async loadData(path, force = false) {
        var _a;
        const _b = get().data, _c = path, current = _b[_c], data = __rest(_b, [typeof _c === "symbol" ? _c : _c + ""]);
        if (!current || (force && !current.loading)) {
            set({
                data: Object.assign(Object.assign({}, data), { [path]: {
                        data: current === null || current === void 0 ? void 0 : current.data,
                        loaded: (_a = current === null || current === void 0 ? void 0 : current.loaded) !== null && _a !== void 0 ? _a : false,
                        loading: true,
                    } }),
            });
            const result = await (0, http_1.getAny)(path);
            set({
                data: Object.assign(Object.assign({}, get().data), { [path]: {
                        data: result,
                        loaded: true,
                        loading: false,
                    } }),
            });
        }
    },
}));
function useMounted() {
    const mounted = (0, react_1.useRef)(false);
    (0, react_1.useEffect)(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);
    return mounted;
}
function usePostData(path, dependencies = []) {
    const add = useStore((m) => m.addData);
    const reset = useStore((m) => m.resetData);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const mounted = useMounted();
    const cb = async (details) => {
        mounted.current && setLoading(true);
        try {
            reset(dependencies);
            return await add(path, details);
        }
        finally {
            mounted.current && setLoading(false);
        }
    };
    return [loading, cb];
}
function useLoadData() {
    return useStore((m) => m.loadData);
}
function useResetData() {
    return useStore((m) => m.resetData);
}
function useGetData(path) {
    const load = useLoadData();
    const result = useStore((m) => m.data[path]);
    // no dependencies on purpose - should re-check every re-render.
    (0, react_1.useEffect)(() => {
        load(path);
    });
    return [(result === null || result === void 0 ? void 0 : result.loaded) || false, result === null || result === void 0 ? void 0 : result.data];
}
function useDeleteData(path, id, dependencies = []) {
    const deleteData = useStore((m) => m.deleteData);
    const reset = useStore((m) => m.resetData);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const mounted = useMounted();
    const cb = async (details) => {
        mounted.current && setLoading(true);
        try {
            reset(dependencies);
            return await deleteData(path, id, details);
        }
        finally {
            mounted.current && setLoading(false);
        }
    };
    return [loading, cb];
}
function useUpdateData(path, id, dependencies = []) {
    const updateData = useStore((m) => m.updateData);
    const reset = useStore((m) => m.resetData);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const mounted = useMounted();
    const cb = async (details) => {
        mounted.current && setLoading(true);
        try {
            reset(dependencies);
            return await updateData(path, id, details);
        }
        finally {
            mounted.current && setLoading(false);
        }
    };
    return [loading, cb];
}
function useSuggestUsers(users) {
    const [, allUsers] = useGetData(`manage/users`);
    return (input) => {
        var _a;
        return ((_a = allUsers === null || allUsers === void 0 ? void 0 : allUsers.items.filter((user) => !users.includes(user.id) && user.name.toLowerCase().includes(input.toLowerCase())).map((user) => ({ value: user.id, display: user.name }))) !== null && _a !== void 0 ? _a : []);
    };
}
function useSearchParam(param) {
    const search = (0, react_router_dom_1.useLocation)().search;
    return new URLSearchParams(search).get(param);
}
function useSearchParamState(param) {
    const location = (0, react_router_dom_1.useLocation)();
    const history = (0, react_router_dom_1.useHistory)();
    const value = useSearchParam(param);
    const setValue = (newValue) => {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set(param, newValue);
        history.replace(Object.assign(Object.assign({}, location), { search: searchParams.toString() }));
    };
    return [value, setValue];
}
function useInterval(callback, ms) {
    (0, react_1.useEffect)(() => {
        const id = setInterval(callback, ms);
        return () => clearInterval(id);
    }, [callback, ms]);
}
function useTab(tabNames) {
    const [selectedTabName, setSelectedTabName] = useSearchParamState('tab');
    const selectedTab = Math.max(0, tabNames.findIndex((tabName) => tabName === selectedTabName));
    selectedTabName !== null && selectedTabName !== void 0 ? selectedTabName : setSelectedTabName(tabNames[selectedTab]);
    const setSelectedTab = (index) => {
        setSelectedTabName(tabNames[index]);
    };
    return [selectedTab, setSelectedTab];
}
